// src/pages/HistorialPaciente.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import Breadcrumb from '../components/Breadcrumb';
import '../styles/HistorialPaciente.css';
import { ToastContainer, toast } from 'react-toastify'; 
import 'react-toastify/dist/ReactToastify.css';
import Pacientes from './Pacientes';
import ModalFirmarConsentimientos from '../components/ModalFirmarConsentimientos';

const CLINICAS = ['LA ELIPA', 'VEN'];
const GENEROS  = ['Hombre', 'Mujer', 'Niña', 'Niño', 'Otro'];


const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: { 'Content-Type': 'application/json' }
});
api.interceptors.request.use(cfg => {
    const token = localStorage.getItem('token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

export default function HistorialPaciente({ isNew=false }) {
    const { expediente } = useParams();
    const history = useHistory();
    const [paciente, setPaciente] = useState({
        n_expediente: '',
        nombre: '',
        primer_apellido: '',
        segundo_apellido: '',
        telefono: '',
        movil: '',
        email: '',
        clinica: '',
        sexo: '',
        fecha_nacimiento: '',
        direccion: '',
        municipio: '',
        n_via: '',
        piso: '',
        letra: '',
        cp: '',
        origen: '',
        incompatibilidades: '',
        alergias: '',
        historia_medica: '',
        notas_y_comentarios: '',
        entidad: '',
        escalera: '',
        alta: '',
        nif: '',
        orto: '',
        presupuesto: '',
        caja: ''
    });
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(isNew);
    const [isDirty, setDirty] = useState(isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);
    const [mostrarModalFirma, setMostrarModalFirma] = useState(false);
  
  // carga inicial
    useEffect(()=> {
        if(isNew){
        api.get('/api/pacientes/next-expediente')
        .then(res => {
            console.log('next-expediente:', res.data);
            setPaciente(prev => ({
                ...prev,
                n_expediente: res.data.toString()
                //n_expediente: '99999'
            }));
            })
            .catch(()=> setError('No puede generar expediente'))
            .finally(()=> setLoading(false));
        } else {
        api.get(`/api/pacientes/${expediente}`)
            .then(res=> setPaciente({ 
            ...res.data,
            fecha_nacimiento: res.data.fecha_nacimiento?.substr(0,10) ?? '',
            alta:             res.data.alta?.substr(0,10) ?? ''
            }))
            .catch(()=> setError('No se pudo cargar'))
            .finally(()=> setLoading(false));
        }
    },[expediente,isNew]);

    const handleSave = async () => {
        setValidationErrors({});
        // validaciones de fechas igual que antes…
        const errs = {};
        // 🧩 Validaciones obligatorias
        if (!paciente.nombre?.trim()) errs.nombre = "El nombre es obligatorio";
        if (!paciente.primer_apellido?.trim()) errs.primer_apellido = "El primer apellido es obligatorio";
        if (!paciente.nif?.trim()) errs.nif = "El NIF es obligatorio";
        if (!paciente.direccion?.trim()) errs.direccion = "La dirección es obligatoria";
        if (!paciente.alta) errs.alta = "La fecha de alta es obligatoria";

        // 🧩 Validar que tenga al menos móvil o email
        const tieneContacto = (paciente.movil && paciente.movil.toString().trim() !== "") ||
                                (paciente.email && paciente.email.trim() !== "");
        if (!tieneContacto) errs.contacto = "Debe tener al menos un móvil o un correo electrónico";
        if (paciente.alta && paciente.fecha_nacimiento && paciente.alta < paciente.fecha_nacimiento) {
            errs.alta = 'La fecha de alta no puede ser anterior a la de nacimiento';
        }
        if (!paciente.clinica?.trim()) errs.clinica = "El campo clinica es obligatorio";

        if(Object.keys(errs).length){
            setValidationErrors(errs);
            toast.error("⚠️ Hay campos obligatorios sin completar o incorrectos");
            return;
        }

        const {
            n_expediente, // ❌ EXCLUIR esto del payload para que no lo cargue
            ...rest
        } = paciente;

        const payload = {
            ...rest,
            //n_expediente: parseInt(paciente.n_expediente, 10),
        
            // Strings sencillos
            nombre: paciente.nombre || null,
            primer_apellido: paciente.primer_apellido || null,
            segundo_apellido: paciente.segundo_apellido || null,
            email: paciente.email || null,
            clinica: paciente.clinica || null,
            sexo: paciente.sexo || null,
            direccion: paciente.direccion || null,
            municipio: paciente.municipio || null,
            letra: paciente.letra || null,
            origen: paciente.origen || null,
            incompatibilidades: paciente.incompatibilidades || null,
            alergias: paciente.alergias || null,
            historia_medica: paciente.historia_medica || null,
            notas_y_comentarios: paciente.notas_y_comentarios || null,
            entidad: paciente.entidad || null,
            escalera: paciente.escalera || null,
            nif: paciente.nif || null,
            orto: paciente.orto || null,
        
            // Fechas en ISO yyyy-MM-dd
            fecha_nacimiento: paciente.fecha_nacimiento || null,
            alta:             paciente.alta             || null,
        
            // Campos numéricos
            telefono:   paciente.telefono   ? parseInt(paciente.telefono, 10)   : null,
            movil:      paciente.movil      ? parseInt(paciente.movil, 10)      : null,
            nVia:       paciente.nVia       ? parseInt(paciente.nVia, 10)       : null,
            piso:       paciente.piso       ? parseInt(paciente.piso, 10)       : null,
            cp:         paciente.cp         ? parseInt(paciente.cp, 10)         : null,
            presupuesto:paciente.presupuesto? parseInt(paciente.presupuesto, 10): null,
            caja:       paciente.caja       ? parseInt(paciente.caja, 10)       : null,
        };
        console.log('⏳ payload a enviar:', payload);    
        setSaving(true); 
        setError(null);

        try {
            if (isNew) {
                const res = await api.post('/api/pacientes', payload);
                const nuevoExp = res.data.n_expediente;
                toast.success('✅ Paciente creado exitosamente');

                setEditMode(false);
                setDirty(false);
                setSaving(false);
                setError(null);

                history.push(`/pacientes/${nuevoExp}`);
            } else {
                await api.put(`/api/pacientes/${expediente}`, payload);
                toast.success('Paciente actualizado exitosamente');

                setEditMode(false);
                setDirty(false);
                setSaving(false);
                setError(null);

                // 🔹 Opcional: recargar datos desde el backend por si el back devuelve algo actualizado
                const { data } = await api.get(`/api/pacientes/${expediente}`);
                setPaciente({
                ...data,
                fecha_nacimiento: data.fecha_nacimiento?.substr(0, 10) ?? '',
                alta: data.alta?.substr(0, 10) ?? ''
                });        
            }
        } catch (err) {
            toast.error('⛔ Error al guardar paciente');
            setSuccessMessage('');
        } finally {
            setSaving(false);
        }
    };

    const cerrarMensaje = () => {
        setSuccessMessage('');
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setPaciente(prev => ({ ...prev, [name]: value }));
        if (!isDirty) setDirty(true);
        // limpiar error de campo al modificar
        setValidationErrors(prev => ({ ...prev, [name]: null }));
    };

    // validación de fechas
    const validateDates = () => {
        const errs = {};
        const { fecha_nacimiento, alta } = paciente;
        const hoy = new Date().setHours(0,0,0,0);

        if (!fecha_nacimiento) {
        errs.fecha_nacimiento = 'Requerida';
        } else {
        const fn = Date.parse(fecha_nacimiento);
        if (isNaN(fn)) errs.fecha_nacimiento = 'Formato inválido';
        else if (fn > hoy) errs.fecha_nacimiento = 'No puede ser futura';
        }

        if (alta) {
        const fa = Date.parse(paciente.alta);
        const fn = Date.parse(paciente.fecha_nacimiento);
        if (isNaN(fa)) errs.alta = 'Formato inválido';
        else if (fn && fa < fn) errs.alta = 'Debe ser ≥ nacimiento';
        }

        return errs;
    };

    const firmar = async (documentoId) => {
        const res = await axios.post(
            `/api/documentos/${documentoId}/enviar-firma`,
            {},
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );

        const url = `${window.location.origin}/firma/${res.data.token}`;
        window.open(url, "_blank"); // se abre en la tablet
    };

    const handleCancel = () => {
        setEditMode(false);
        setDirty(false);
        setError(null);
        setLoading(true);
        // recargar datos originales
        api.get(`/api/pacientes/${expediente}`)
        .then(res => {
            setPaciente({
            ...res.data,
            fecha_nacimiento: res.data.fecha_nacimiento?.substring(0,10) || '',
            alta:              res.data.alta?.substring(0,10) || ''
            });
            setValidationErrors({});
        })
        .finally(() => setLoading(false));
    };

    const handleBack = () => history.push('/pacientes');

    if (loading) return <p className="loading">Cargando datos…</p>;
    if (error)   return <p className="error">{error}</p>;

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="paciente-form-page">
                <Breadcrumb />
                <h2>
                    Paciente - {" "}
                    {paciente
                        ? [paciente.nombre, paciente.primer_apellido, paciente.segundo_apellido]
                            .filter(Boolean)        // elimina null, undefined o strings vacíos
                            .join(" ")
                        : expediente}
                </h2>
                <div className="form-buttons top">
                    {!editMode && (
                    <button className="edit" onClick={() => setEditMode(true)}>
                        Editar
                    </button>
                    )}
                    {editMode && (
                    <>
                        <button
                        className="save"
                        disabled={!isDirty || saving}
                        onClick={handleSave}
                        >
                        {saving ? 'Guardando…' : 'Guardar'}
                        </button>
                        {successMessage && (
                            <div className="alert-success">
                                <span>{successMessage}</span>
                                <button className="alert-close" onClick={cerrarMensaje}>×</button>
                            </div>
                        )}
                        {error && (
                            <div className="alert-error">
                            <span>{error}</span>
                            </div>
                        )}
                        <button className="cancel" onClick={handleCancel} disabled={saving}>
                        Cancelar
                        </button>
                    </>
                    )}
                    <button className="back" onClick={handleBack}>
                    Volver
                    </button>
                </div>

                <form className="paciente-form" onSubmit={e => e.preventDefault()}>
                    {/* SECCIÓN 1: DATOS PERSONALES */}
                    <div className="form-section">
                        <h3><span className="icon">👤</span> Datos Personales</h3>
                        <div className="section-grid">
                            <label>Nº Expediente <input name="n_expediente" value={paciente.n_expediente} readOnly className="readonly-input"/></label>
                            <label>NIF <input name="nif" value={paciente.nif} onChange={handleChange} disabled={!editMode} 
                                className={validationErrors.nif ? 'error-field' : ''}/>
                                {validationErrors.nif && <span className="error-text">{validationErrors.nif}</span>}</label>
                            <label>Nombre <input name="nombre" value={paciente.nombre} onChange={handleChange} disabled={!editMode} 
                                className={validationErrors.nombre ? 'error-field' : ''}/>
                                {validationErrors.nombre && <span className="error-text">{validationErrors.nombre}</span>}
                            </label>
                            <label>1er apellido <input name="primer_apellido" value={paciente.primer_apellido} onChange={handleChange} disabled={!editMode}
                                className={validationErrors.primer_apellido ? 'error-field' : ''}/>
                                {validationErrors.primer_apellido && <span className="error-text">{validationErrors.primer_apellido}</span>}
                            </label>
                            <label>2º apellido <input name="segundo_apellido" value={paciente.segundo_apellido} onChange={handleChange} disabled={!editMode} /></label>
                            <label>Género 
                                <select name="sexo" value={paciente.sexo} onChange={handleChange} disabled={!editMode}>
                                    <option value="">— Selecciona —</option>
                                    {GENEROS.map(g => <option key={g} value={g}>{g}</option>)}
                                </select>
                            </label>
                        </div>
                    </div>

                    {/* SECCIÓN 2: CONTACTO Y CLÍNICA */}
                    <div className="form-section">
                        <h3><span className="icon">📞</span> Contacto y Registro</h3>
                        <div className="section-grid">
                            <label>Teléfono <input name="telefono" value={paciente.telefono} onChange={handleChange} disabled={!editMode} 
                                className={validationErrors.telefono ? 'error-field' : ''}/>
                                {validationErrors.telefono && <span className="error-text">{validationErrors.telefono}</span>}
                            </label>
                            <label>Móvil <input name="movil" value={paciente.movil} onChange={handleChange} disabled={!editMode} 
                                className={validationErrors.movil ? 'error-field' : ''}/>
                                {validationErrors.movil && <span className="error-text">{validationErrors.movil}</span>}
                            </label>
                            <label>Email <input name="email" value={paciente.email} onChange={handleChange} disabled={!editMode} 
                                className={validationErrors.email ? 'error-field' : ''}/>
                                {validationErrors.email && <span className="error-text">{validationErrors.email}</span>}
                            </label>
                            <label>Clínica 
                                <select name="clinica" value={paciente.clinica} onChange={handleChange} disabled={!editMode} 
                                className={validationErrors.clinica ? 'error-field' : ''}>
                                    <option value="">— Selecciona —</option>
                                    {CLINICAS.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                {validationErrors.clinica && <span className="error-text">{validationErrors.clinica}</span>}
                            </label>
                            <label>Fecha Nacimiento <input type="date" name="fecha_nacimiento" value={paciente.fecha_nacimiento} onChange={handleChange} disabled={!editMode} 
                                className={validationErrors.fecha_nacimiento ? 'error-field' : ''}/>
                                {validationErrors.fecha_nacimiento && <span className="error-text">{validationErrors.fecha_nacimiento}</span>}
                            </label>
                            <label>Alta <input type="date" name="alta" value={paciente.alta} onChange={handleChange} disabled={!editMode} 
                                className={validationErrors.alta ? 'error-field' : ''}/>
                                {validationErrors.alta && <span className="error-text">{validationErrors.alta}</span>}
                            </label>
                        </div>
                    </div>

                    {/* SECCIÓN 3: DIRECCIÓN (Grid más compacto) */}
                    <div className="form-section full-width">
                        <h3><span className="icon">📍</span> Dirección</h3>
                        <div className="section-grid address-grid">
                            <label className="span-2">Calle/Dirección <input name="direccion" value={paciente.direccion} onChange={handleChange} disabled={!editMode} /></label>
                            <label>Municipio <input name="municipio" value={paciente.municipio} onChange={handleChange} disabled={!editMode} /></label>
                            <label>Nº Vía <input name="n_via" value={paciente.n_via} onChange={handleChange} disabled={!editMode} /></label>
                            <label>Piso <input name="piso" value={paciente.piso} onChange={handleChange} disabled={!editMode} /></label>
                            <label>Letra <input name="letra" value={paciente.letra} onChange={handleChange} disabled={!editMode} /></label>
                            <label>CP <input name="cp" value={paciente.cp} onChange={handleChange} disabled={!editMode} /></label>
                        </div>
                    </div>

                    {/* SECCIÓN 4: INFORMACIÓN MÉDICA (Textareas) */}
                    <div className="form-section full-width">
                        <h3><span className="icon">🏥</span> Información Médica y Notas</h3>
                        <div className="section-grid textarea-grid">
                            <label>Incompatibilidades/Últimos 14 días <textarea name="incompatibilidades" value={paciente.incompatibilidades} onChange={handleChange} disabled={!editMode} /></label>
                            <label>Alergias <textarea name="alergias" value={paciente.alergias} onChange={handleChange} disabled={!editMode} /></label>
                            <label>Historia Médica <textarea name="historia_medica" value={paciente.historia_medica} onChange={handleChange} disabled={!editMode} /></label>
                            <label>Notas y Comentarios <textarea name="notas_y_comentarios" value={paciente.notas_y_comentarios} onChange={handleChange} disabled={!editMode} /></label>
                        </div>
                    </div>
                </form>
                <div className="acciones-paciente">
                    <button
                        className="accion-btn"
                        onClick={() => {
                            history.push(`/pacientes/${paciente.n_expediente}/historial-clinico`)}
                        }
                    >
                        Historial Clínico
                    </button>
                    <button
                        className="accion-btn"
                        onClick={() => {
                            history.push(`/pacientes/${paciente.n_expediente}/historial-economico`)}
                        }
                    >
                        Historial Económico
                    </button>
                    <button
                        className="accion-btn"
                        onClick={() => history.push(`/pacientes/${paciente.n_expediente}/odontograma`)}
                    >
                        Odontograma
                    </button>
                        <button
                            className="accion-btn accion-btn-imagenes"
                            onClick={() => history.push(`/pacientes/${paciente.n_expediente}/imagenes`)}
                        >
                        🖼️ Imágenes
                    </button>
                    <button 
                        className="accion-btn"
                        onClick={() => history.push(`/pacientes/${paciente.n_expediente}/documentos`)}
                    > 
                        📜 Documentos</button>
                    <button 
                        className="accion-btn"
                        onClick={() => setMostrarModalFirma(true)}
                    > 
                        ✔️ Firmar consentimiento
                    </button>
                </div>
            </div>
            <ModalFirmarConsentimientos
                abierto={mostrarModalFirma}
                expediente={expediente}
                onClose={() => setMostrarModalFirma(false)}
            />
        </>
    );
}
